/**
 * VerificationDOMIUM - Portail public de vérification DOMIUM
 * COPPAM – Garanties Solidaires
 *
 * Accessible sans authentification via QR code :
 *  - DRAFT  : "Document non contractuel" (rouge) + référence
 *  - ACTIVE : "ACTIVE" (vert) + référence + date signature + fin de garantie (+10 ans)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Shield, CheckCircle, XCircle, FileCheck, Calendar,
  Search, Home, AlertTriangle, ShieldAlert, ShieldCheck, Clock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ESS_COLORS = {
  vertPrincipal: '#0A7F5A',
  bleuInstitutionnel: '#0B3C5D',
  sableClair: '#F4F6F3',
  rougeAlerte: '#DC2626',
};

const formatDateFr = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch {
    return iso;
  }
};

const formatDateTimeFr = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return iso;
  }
};

export default function VerificationDOMIUM() {
  const { reference } = useParams();
  const [searchParams] = useSearchParams();

  // État "statut public" (DRAFT/ACTIVE/NOT_FOUND) -- auto-chargé via QR
  const [publicStatus, setPublicStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // État "vérification OTP approfondie" (optionnelle, saisie manuelle du code)
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepResult, setDeepResult] = useState(null);
  const [deepError, setDeepError] = useState(null);
  const [refInput, setRefInput] = useState('');
  const [codeInput, setCodeInput] = useState('');

  const codeFromUrl = searchParams.get('code') || '';
  const refFromUrl = reference || searchParams.get('ref') || '';

  // 1) Chargement du statut public (DRAFT / ACTIVE) dès qu'une référence est fournie
  const loadPublicStatus = useCallback(async (ref) => {
    if (!ref) return;
    setLoadingStatus(true);
    setStatusError(null);
    setPublicStatus(null);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/domium/verify-public/${encodeURIComponent(ref)}`
      );
      setPublicStatus(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Impossible de récupérer le statut du document.";
      setStatusError(detail);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  // 2) Vérification OTP approfondie (signature électronique)
  const verifyDeepSignature = async (ref, code) => {
    if (!ref || !code) {
      setDeepError("Référence et code de vérification requis.");
      return;
    }
    setDeepLoading(true);
    setDeepError(null);
    setDeepResult(null);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/domium/signature/verify/${encodeURIComponent(ref)}`,
        { params: { code } }
      );
      setDeepResult(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error;
      setDeepError(detail || "Erreur lors de la vérification.");
    } finally {
      setDeepLoading(false);
    }
  };

  useEffect(() => {
    if (refFromUrl) {
      setRefInput(refFromUrl);
      loadPublicStatus(refFromUrl);
    }
    if (codeFromUrl) {
      setCodeInput(codeFromUrl);
    }
    if (refFromUrl && codeFromUrl) {
      verifyDeepSignature(refFromUrl, codeFromUrl);
    }
  }, [refFromUrl, codeFromUrl, loadPublicStatus]);

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (refInput) loadPublicStatus(refInput);
    if (refInput && codeInput) verifyDeepSignature(refInput, codeInput);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: ESS_COLORS.sableClair }}>
      {/* Header */}
      <div className="py-6 px-4" style={{ backgroundColor: ESS_COLORS.bleuInstitutionnel }}>
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Shield className="h-8 w-8 text-white" />
          <div>
            <h1 className="text-xl font-bold text-white">Vérification de document DOMIUM</h1>
            <p className="text-sm text-blue-200">COPPAM – Garanties Solidaires</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
        {/* === BLOC 1 : STATUT PUBLIC (chargé automatiquement via QR) === */}
        {loadingStatus && (
          <Card data-testid="verify-status-loading">
            <CardContent className="pt-6 flex items-center gap-3">
              <Clock className="h-5 w-5 animate-pulse text-gray-500" />
              <p className="text-gray-600">Récupération du statut du document…</p>
            </CardContent>
          </Card>
        )}

        {statusError && !loadingStatus && (
          <Card data-testid="verify-status-error" className="border-2 border-red-300">
            <CardContent className="pt-6 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <p className="text-red-600">{statusError}</p>
            </CardContent>
          </Card>
        )}

        {/* Document introuvable */}
        {publicStatus && !publicStatus.found && (
          <Card data-testid="verify-notfound-card" className="border-2 border-gray-300">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-gray-100">
                  <XCircle className="h-8 w-8 text-gray-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-700">Document introuvable</h3>
                  <p className="text-sm text-gray-500">
                    Aucun document DOMIUM ne correspond à la référence
                    <span className="font-mono ml-1">{publicStatus.reference}</span>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statut DRAFT : rouge "Document non contractuel" */}
        {publicStatus && publicStatus.found && publicStatus.is_draft && (
          <Card
            data-testid="verify-draft-card"
            className="border-2"
            style={{ borderColor: ESS_COLORS.rougeAlerte, backgroundColor: '#FEF2F2' }}
          >
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full" style={{ backgroundColor: '#FEE2E2' }}>
                  <ShieldAlert className="h-8 w-8" style={{ color: ESS_COLORS.rougeAlerte }} />
                </div>
                <div>
                  <h3
                    className="text-2xl font-bold"
                    style={{ color: ESS_COLORS.rougeAlerte }}
                    data-testid="verify-draft-label"
                  >
                    Document non contractuel
                  </h3>
                  <p className="text-sm text-gray-600">
                    Ce document est un aperçu provisoire (DRAFT) — aucune valeur juridique.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-red-200 flex items-start gap-2">
                <FileCheck className="h-4 w-4 mt-1 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Référence du document</p>
                  <p
                    className="font-mono font-semibold text-base"
                    data-testid="verify-draft-reference"
                  >
                    {publicStatus.reference}
                  </p>
                </div>
              </div>

              <Badge
                className="text-white px-3 py-1"
                style={{ backgroundColor: ESS_COLORS.rougeAlerte }}
                data-testid="verify-draft-badge"
              >
                Statut : DRAFT
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Statut ACTIVE : vert + date signature + fin garantie +10 ans */}
        {publicStatus && publicStatus.found && publicStatus.is_active && (
          <Card
            data-testid="verify-active-card"
            className="border-2"
            style={{ borderColor: ESS_COLORS.vertPrincipal, backgroundColor: '#F0FDF4' }}
          >
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div
                  className="p-3 rounded-full"
                  style={{ backgroundColor: `${ESS_COLORS.vertPrincipal}20` }}
                >
                  <ShieldCheck
                    className="h-8 w-8"
                    style={{ color: ESS_COLORS.vertPrincipal }}
                  />
                </div>
                <div>
                  <h3
                    className="text-2xl font-bold"
                    style={{ color: ESS_COLORS.vertPrincipal }}
                    data-testid="verify-active-label"
                  >
                    ACTIVE
                  </h3>
                  <p className="text-sm text-gray-600">
                    Document authentique, garantie en cours de validité.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-green-200">
                <div className="flex items-start gap-2">
                  <FileCheck className="h-4 w-4 mt-1 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Référence du document</p>
                    <p
                      className="font-mono font-semibold"
                      data-testid="verify-active-reference"
                    >
                      {publicStatus.reference}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 mt-1 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Date de signature</p>
                    <p data-testid="verify-active-signed-at">
                      {formatDateTimeFr(publicStatus.signed_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-1 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">
                      Fin de garantie (+{publicStatus.garantie_duree_annees || 10} ans)
                    </p>
                    <p
                      className="font-semibold"
                      style={{ color: ESS_COLORS.vertPrincipal }}
                      data-testid="verify-active-garantie-fin"
                    >
                      {formatDateFr(publicStatus.garantie_fin)}
                    </p>
                  </div>
                </div>
                {publicStatus.signer_name && (
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 mt-1 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Signataire</p>
                      <p data-testid="verify-active-signer">
                        {publicStatus.signer_name}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Badge
                className="text-white px-3 py-1"
                style={{ backgroundColor: ESS_COLORS.vertPrincipal }}
                data-testid="verify-active-badge"
              >
                Statut : ACTIVE
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* === BLOC 2 : Formulaire de recherche manuelle === */}
        <Card data-testid="verify-form-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="h-5 w-5" />
              Vérification manuelle d'un document
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleManualSearch} className="space-y-3">
              <Input
                data-testid="verify-ref-input"
                placeholder="Référence du document (ex: DOM-20260101-ABC123)"
                value={refInput}
                onChange={(e) => setRefInput(e.target.value)}
              />
              <Input
                data-testid="verify-code-input"
                placeholder="Code de vérification (optionnel, pour vérification approfondie)"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
              />
              <Button
                data-testid="verify-submit-btn"
                type="submit"
                disabled={loadingStatus || deepLoading || !refInput}
                className="w-full text-white"
                style={{ backgroundColor: ESS_COLORS.vertPrincipal }}
              >
                {loadingStatus || deepLoading ? 'Vérification...' : 'Vérifier le document'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* === BLOC 3 : Résultat de la vérification approfondie (OTP) === */}
        {deepResult && (
          <Card
            data-testid="verify-result-card"
            className="border-2"
            style={{
              borderColor: deepResult.valid ? ESS_COLORS.vertPrincipal : ESS_COLORS.rougeAlerte
            }}
          >
            <CardContent className="pt-6">
              {deepResult.valid ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle
                      className="h-7 w-7"
                      style={{ color: ESS_COLORS.vertPrincipal }}
                    />
                    <div>
                      <h3
                        className="text-lg font-bold"
                        style={{ color: ESS_COLORS.vertPrincipal }}
                      >
                        Signature authentique et valide
                      </h3>
                      <p className="text-xs text-gray-500">
                        Vérification approfondie réussie (mode : {deepResult.mode})
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 pl-10">
                    Réf. signature :{' '}
                    <span className="font-mono" data-testid="verify-sig-ref">
                      {deepResult.signature_reference}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <XCircle className="h-7 w-7 text-red-500" />
                  <div>
                    <h3 className="text-lg font-bold text-red-600">Vérification échouée</h3>
                    <p className="text-sm text-gray-500">
                      {deepResult.error || 'Le code de vérification ne correspond pas.'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {deepError && !deepResult && (
          <Card data-testid="verify-error-card" className="border-2 border-red-300">
            <CardContent className="pt-6 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <p className="text-red-600">{deepError}</p>
            </CardContent>
          </Card>
        )}

        {/* Lien retour */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => (window.location.href = '/')}
            className="text-gray-500"
            data-testid="verify-home-btn"
          >
            <Home className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
}
